import React, { useState, useContext, useEffect, useCallback } from "react";
import {
  makeStyles,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@material-ui/core";
import NotificationsIcon from "@material-ui/icons/Notifications";
import CachedIcon from "@material-ui/icons/Cached";
import api from "../services/api";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BirthdayModal from "../components/BirthdayModal";
import ChatPopover from "../pages/Chat/ChatPopover";
import { useDate } from "../hooks/useDate";
import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import useSocketListener from "../hooks/useSocketListener";
import { FaGlobe } from "react-icons/fa";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}40`,
      borderRadius: "8px",
      fontWeight: 600,
      textTransform: "none",
      transition: "all 0.3s ease",
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}10`,
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
  },

  appBar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.drawer + 1,
  },

  toolbar: {
    paddingLeft: 20,
    paddingRight: 16,
    background: theme.palette.barraSuperior,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    minHeight: 62,
    height: 62,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  logoWrapper: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    paddingLeft: 4,
    flex: "0 0 auto",
  },

  logo: {
    height: 28,
    width: "auto",
    maxWidth: 130,
    objectFit: "contain",
    objectPosition: "left center",
    flexShrink: 0,
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
  },

  // ícones centralizados
  allIcons: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flex: "1 1 auto",
    justifyContent: "center",
    // Uniformiza TODOS os IconButtons dentro da barra
    "& .MuiIconButton-root": {
      padding: 7,
      borderRadius: 6,
      color: theme.palette.headerText,
      opacity: 0.88,
      "&:hover": {
        backgroundColor: theme.mode === "light" ? "rgba(255,255,255,0.15)" : "rgba(232,168,158,0.12)",
        opacity: 1,
        color: theme.palette.headerText,
      },
    },
    "& .MuiSvgIcon-root": {
      fontSize: "20px !important",
    },
    "& svg": {
      fontSize: "20px !important",
      width: 20,
      height: 20,
    },
  },

  // separador vertical entre grupos de ícones
  iconDivider: {
    width: 1,
    height: 22,
    backgroundColor: theme.mode === "light" ? "rgba(255,255,255,0.25)" : "rgba(232,168,158,0.2)",
    margin: "0 6px",
    flexShrink: 0,
  },

  // avatar à direita
  avatarWrapper: {
    display: "flex",
    alignItems: "center",
    flex: "0 0 auto",
  },

  // estilo único para TODOS os icon buttons da toolbar
  toolbarIconBtn: {
    color: theme.palette.headerText,
    opacity: 0.88,
    borderRadius: 6,
    padding: 7,
    "& .MuiSvgIcon-root": {
      fontSize: 20,
    },
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "rgba(255,255,255,0.15)" : "rgba(232,168,158,0.12)",
      opacity: 1,
      color: theme.palette.headerText,
    },
  },

  // alias para compatibilidade com MainListItems
  navWrapper: {
    display: "flex",
    alignItems: "center",
  },

  rightActions: {
    display: "flex",
    alignItems: "center",
    gap: 0,
  },

  appBarSpacer: {
    minHeight: 62,
    flexShrink: 0,
  },

  content: {
    flex: 1,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border: theme.mode === "light" ? "2px solid rgba(255,255,255,0.4)" : "2px solid rgba(232,168,158,0.3)",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: theme.mode === "light" ? "rgba(255,255,255,0.9)" : "rgba(232,168,158,0.7)",
      transform: "scale(1.05)",
    },
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": { transform: "scale(.8)", opacity: 1 },
    "100%": { transform: "scale(2.4)", opacity: 0 },
  },
}))(Badge);

const LoggedInLayout = ({ children, hideMenu = false }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, user, socket } = useContext(AuthContext);

  const [showOptions, setShowOptions] = useState(false);
  const [showAnnouncementsModal, setShowAnnouncementsModal] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);

  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
  const { getAll } = useSettings();

  const LANGUAGE_OPTIONS = [
    { code: "pt-BR", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "ar", label: "عربي" },
  ];

  useEffect(() => {
    async function fetchSettings() {
      try {
        const settings = await getAll();
        const enabledLanguagesSetting = settings.find(
          (s) => s.key === "enabledLanguages"
        )?.value;
        let langs = ["pt-BR", "en"];
        try {
          if (enabledLanguagesSetting) langs = JSON.parse(enabledLanguagesSetting);
        } catch {}
        setEnabledLanguages(langs);
      } catch {}
    }
    fetchSettings();
  }, [user?.companyId]);

  const filteredLanguageOptions = LANGUAGE_OPTIONS.filter((lang) =>
    enabledLanguages.includes(lang.code)
  );

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await api.get("/announcements/for-company", {
          params: { status: true, pageNumber: "1" },
        });
        const activeAnnouncements = data.records.filter((a) => {
          const isActive = a.status === true || a.status === "true";
          const isNotExpired = !a.expiresAt || new Date(a.expiresAt) > new Date();
          return isActive && isNotExpired;
        });
        setAnnouncements(activeAnnouncements);
        if (activeAnnouncements.length > 0) setShowAnnouncementsModal(true);
      } catch (err) {
        toastError(err);
      }
    };
    if (user?.id) fetchAnnouncements();
  }, [user?.id]);

  useEffect(() => {
    const companyId = user?.companyId;
    if (companyId) {
      const savedProfileImage = localStorage.getItem("profileImage");
      const currentProfileImage = savedProfileImage || user.profileImage;
      if (currentProfileImage) {
        setProfileUrl(`${backendUrl}/public/company${companyId}/user/${currentProfileImage}`);
      } else {
        setProfileUrl(`${backendUrl}/public/app/noimage.png`);
      }
    }
  }, [user?.companyId, user?.profileImage]);

  const handleAuthEvent = useCallback(
    (data) => {
      if (data.user.id === +user?.id) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    },
    [user?.id]
  );

  const handleUserUpdate = useCallback(
    (data) => {
      if (data.action === "update" && data.user.id === +user?.id) {
        if (data.user.profileImage) {
          const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
          setProfileUrl(newProfileUrl);
          localStorage.setItem("profileImage", data.user.profileImage);
        }
      }
    },
    [user?.companyId, user?.id]
  );

  const handleUserBirthday = useCallback(
    (data) => {
      if (data.userId === +user?.id) setShowBirthdayModal(true);
    },
    [user?.id]
  );

  const handleContactBirthday = useCallback(() => {}, []);

  const checkBirthdaysOnLogin = useCallback(async () => {
    if (user?.id && user?.companyId) {
      try {
        const { data } = await api.get("/birthdays/today");
        const userBirthday = data.data.users.find((u) => u.id === +user.id);
        if (userBirthday) setShowBirthdayModal(true);
      } catch {}
    }
  }, [user?.id, user?.companyId]);

  useSocketListener(socket, user, "auth", handleAuthEvent);
  useSocketListener(socket, user, "user", handleUserUpdate);
  useSocketListener(socket, user, "user-birthday", handleUserBirthday);
  useSocketListener(socket, user, "contact-birthday", handleContactBirthday);

  useEffect(() => {
    if (user?.id && user?.companyId) {
      const timer = setTimeout(() => checkBirthdaysOnLogin(), 1000);
      return () => clearTimeout(timer);
    }
  }, [user?.id, user?.companyId, checkBirthdaysOnLogin]);

  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");
      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);
      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleRefreshPage = () => window.location.reload(false);

  const handleLanguageChange = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    window.location.reload();
  };

  if (loading || updateInProgress) return <BackdropLoading />;

  return (
    <div className={classes.root + " logged-in-layout"}>
      {!hideMenu && (
        <AppBar className={classes.appBar} elevation={0}>
          <Toolbar disableGutters className={classes.toolbar}>
            {/* Logo - esquerda */}
            <div className={classes.logoWrapper}>
              <img className={classes.logo} alt="logo" />
            </div>

            {/* Ícones centralizados */}
            <div className={classes.allIcons}>
              {/* Grupo de navegação */}
              <MainListItems />

              {/* Separador */}
              <div className={classes.iconDivider} />

              {/* Grupo de utilitários */}
              {/* Language */}
              <div style={{ position: "relative" }}>
                <IconButton
                  size="small"
                  className={classes.toolbarIconBtn}
                  onClick={() => setShowOptions(!showOptions)}
                >
                  <FaGlobe style={{ fontSize: 18 }} />
                </IconButton>
                {showOptions && (
                  <>
                  <div
                    style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1299 }}
                    onClick={() => setShowOptions(false)}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 40,
                      right: 0,
                      background: theme.mode === "light" ? "#fff" : "#232327",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                      borderRadius: 10,
                      padding: 8,
                      zIndex: 1300,
                      minWidth: 130,
                    }}
                  >
                    {filteredLanguageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                          padding: "6px 10px",
                          textAlign: "left",
                          borderRadius: 6,
                          fontSize: 14,
                          color: theme.mode === "light" ? "#374151" : "#d1d5db",
                        }}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                  </>
                )}
              </div>

              {/* Theme toggle */}
              <IconButton
                size="small"
                className={classes.toolbarIconBtn}
                onClick={colorMode.toggleColorMode}
              >
                {theme.mode === "dark" ? (
                  <Brightness7Icon style={{ fontSize: 20 }} />
                ) : (
                  <Brightness4Icon style={{ fontSize: 20 }} />
                )}
              </IconButton>

              {/* Volume */}
              <NotificationsVolume setVolume={setVolume} volume={volume} />

              {/* Refresh */}
              <IconButton
                size="small"
                className={classes.toolbarIconBtn}
                onClick={handleRefreshPage}
              >
                <CachedIcon style={{ fontSize: 20 }} />
              </IconButton>

              {/* Notifications */}
              {user.id && <NotificationsPopOver volume={volume} />}

              {/* Announcements */}
              <AnnouncementsPopover />

              {/* Chat */}
              <ChatPopover />
            </div>

            {/* Avatar fixo à direita */}
            <div className={classes.avatarWrapper}>
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                variant="dot"
                onClick={handleMenu}
                style={{ cursor: "pointer" }}
              >
                <Avatar
                  alt="user"
                  className={classes.avatar2}
                  src={profileUrl}
                />
              </StyledBadge>

              <UserModal
                open={userModalOpen}
                onClose={() => setUserModalOpen(false)}
                onImageUpdate={(url) => setProfileUrl(url)}
                userId={user?.id}
              />

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                open={menuOpen}
                onClose={handleCloseMenu}
                PaperProps={{
                  style: { borderRadius: 10, minWidth: 150 },
                }}
              >
                <MenuItem onClick={handleOpenUserModal}>
                  {i18n.t("mainDrawer.appBar.user.profile")}
                </MenuItem>
                <MenuItem onClick={handleClickLogout}>
                  {i18n.t("mainDrawer.appBar.user.logout")}
                </MenuItem>
              </Menu>
            </div>
          </Toolbar>
        </AppBar>
      )}

      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>

      {/* Modal de Informativos */}
      <Dialog
        open={showAnnouncementsModal}
        onClose={() => setShowAnnouncementsModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Informativos</DialogTitle>
        <DialogContent dividers>
          {selectedAnnouncement ? (
            <div>
              <Typography variant="h6" gutterBottom>
                {selectedAnnouncement.title}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
                {selectedAnnouncement.text}
              </Typography>
              {selectedAnnouncement.mediaPath && (
                <div style={{ marginTop: 16 }}>
                  <img
                    src={`${backendUrl}/public/company${user.companyId}${selectedAnnouncement.mediaPath}`}
                    alt="Anexo"
                    style={{ maxWidth: "100%" }}
                  />
                </div>
              )}
              <Button
                onClick={() => setSelectedAnnouncement(null)}
                style={{ marginTop: 16 }}
                variant="outlined"
              >
                Voltar para lista
              </Button>
            </div>
          ) : (
            <List>
              {announcements.map((announcement) => (
                <ListItem
                  button
                  key={announcement.id}
                  onClick={() => setSelectedAnnouncement(announcement)}
                >
                  <ListItemAvatar>
                    <Avatar>
                      <NotificationsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={announcement.title}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="textPrimary">
                          Prioridade:{" "}
                          {announcement.priority === 1
                            ? "Alta"
                            : announcement.priority === 2
                            ? "Média"
                            : "Baixa"}
                        </Typography>
                        {` — ${new Date(announcement.createdAt).toLocaleDateString()}`}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAnnouncementsModal(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Aniversário */}
      <BirthdayModal
        open={showBirthdayModal}
        onClose={() => setShowBirthdayModal(false)}
        user={user}
      />
    </div>
  );
};

export default LoggedInLayout;
